<template>
    <div class="class-1">
      <!-- Header -->
      <AppHeader />
  
      <!-- Template Download/Upload Section -->
      <div class="container mt-4">
        <div class="row">
          <div class="col-12">
            <div class="card mb-4">
              <div class="card-body">
                <h5 class="card-title">Bulk Product Operations</h5>
                <div class="d-flex gap-2">
                  <button 
                    type="button" 
                    class="btn btn-success" 
                    @click="downloadTemplate"
                  >
                    <i class="fas fa-download"></i> Download Template
                  </button>
                  <div class="position-relative">
                    <button 
                      type="button" 
                      class="btn btn-primary" 
                      @click="triggerFileUpload"
                    >
                      <i class="fas fa-upload"></i> Upload & Add Products
                    </button>
                    <input
                      type="file"
                      ref="fileInput"
                      class="d-none"
                      accept=".xlsx, .xls, .csv"
                      @change="handleTemplateUpload"
                    >
                  </div>
                </div>
                
                <!-- Progress Bar -->
                <div v-if="uploadProgress > 0 && uploadProgress < 100" class="mt-3">
                  <div class="progress">
                    <div 
                      class="progress-bar" 
                      role="progressbar" 
                      :style="{ width: uploadProgress + '%' }"
                      :aria-valuenow="uploadProgress" 
                      aria-valuemin="0" 
                      aria-valuemax="100"
                    >
                      {{ uploadProgress }}%
                    </div>
                  </div>
                </div>
                
                <!-- Upload Results Summary -->
                <div v-if="uploadResults.show" class="mt-3">
                  <div class="alert" :class="uploadResults.successCount > 0 ? 'alert-success' : 'alert-warning'">
                    <h6>Upload Results:</h6>
                    <p>Successfully added: {{ uploadResults.successCount }} products</p>
                    <p v-if="uploadResults.errorCount > 0">Failed: {{ uploadResults.errorCount }} products</p>
                    <button 
                      v-if="uploadResults.errors.length > 0" 
                      class="btn btn-sm btn-link" 
                      @click="showErrorDetails = !showErrorDetails"
                    >
                      {{ showErrorDetails ? 'Hide' : 'Show' }} Details
                    </button>
                    <div v-if="showErrorDetails && uploadResults.errors.length > 0" class="mt-2">
                      <div v-for="(error, index) in uploadResults.errors" :key="index" class="text-danger small">
                        {{ error }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <!-- Product Form -->
      <div class="container mt-4">
        <div class="row">
          <div class="col-12">
            <h2>Product Details</h2>
            <form @submit.prevent="submitForm">
              <!-- Name Inputs for Each Language -->
              <div class="form-row" v-for="language in languages" :key="language.id">
                <div class="form-group col-md-12">
                  <label :for="`name_${language.id}`">Name ({{ language.fullName }}) *</label>
                  <input
                    type="text"
                    class="form-control"
                    :id="`name_${language.id}`"
                    :placeholder="`Enter Product Name in ${language.fullName}`"
                    v-model="form.names[language.id]"
                    required
                  >
                </div>
              </div>
  
              <!-- Price -->
              <div class="form-row">
                <div class="form-group col-md-12">
                  <label for="price">Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    class="form-control"
                    id="price"
                    v-model="form.price"
                    required
                  >
                </div>
              </div>
  
              <!-- ERP Item -->
              <div class="form-row">
                <div class="form-group col-md-12">
                  <label for="erpItem">ERP Item Code</label>
                  <input
                    type="text"
                    class="form-control"
                    id="erpItem"
                    v-model="form.ERP_Item"
                  >
                </div>
              </div>
              
              <!-- barcode -->
              <div class="form-row">
                <div class="form-group col-md-12">
                  <label for="barcode">BarCode</label>
                  <input
                    type="text"
                    class="form-control"
                    id="barcode"
                    v-model="form.barcode"
                  >
                </div>
              </div>
              
              <!-- Barcode Type -->
              <div class="form-row">
                <div class="form-group col-md-12">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="hasBarcode"
                      v-model="hasBarcode"
                      @change="handleBarcodeToggle"
                    >
                    <label class="form-check-label" for="hasBarcode">Has Barcode</label>
                  </div>
                  
                  <div v-if="hasBarcode" class="ml-4 mt-2">
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="radio"
                        id="barcodeTypeQuantity"
                        v-model="form.barcode_type"
                        :value="0"
                        :disabled="!hasBarcode"
                      >
                      <label class="form-check-label" for="barcodeTypeQuantity">Quantity</label>
                    </div>
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="radio"
                        id="barcodeTypeRate"
                        v-model="form.barcode_type"
                        :value="1"
                        :disabled="!hasBarcode"
                      >
                      <label class="form-check-label" for="barcodeTypeRate">Rate</label>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Status Checkboxes -->
              <div class="form-row">
                <div class="form-group col-md-12">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="isSuspend"
                      v-model="form.isSuspend"
                    >
                    <label class="form-check-label" for="isSuspend">Suspend Product</label>
                  </div>
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="isHotKey"
                      v-model="form.isHotKey"
                    >
                    <label class="form-check-label" for="isHotKey">Hot Key Item</label>
                  </div>
                </div>
              </div>
  
              <!-- Product Image -->
              <div class="form-row">
                <div class="form-group col-md-12">
                  <label for="productImage">Product Image</label>
                  <input
                    type="file"
                    class="form-control-file"
                    id="productImage"
                    @change="handleImageUpload"
                  >
                  <img 
                    v-if="imagePreview" 
                    :src="imagePreview" 
                    class="mt-2 img-thumbnail" 
                    style="max-height: 200px;"
                  >
                </div>
              </div>
  
              <!-- Buttons -->
              <div class="form-row">
                <div class="form-group col-12 d-flex justify-content-between">
                  <button type="button" class="btn btn-secondary" @click="cancelForm">Cancel</button>
                  <button type="submit" class="btn addproduct">Add Product</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
</template>
  
<script>
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default {
  data() {
    return {
      languages: [],
      categories: [],
      products: [],
      currentDate: "",
      currentTime: "",
      imagePreview: null,
      hasBarcode: false,
      uploadProgress: 0,
      uploadResults: {
        show: false,
        successCount: 0,
        errorCount: 0,
        errors: []
      },
      showErrorDetails: false,
      form: {
        catId: "",
        cost: "0",
        price: "",
        prepare: "0",
        image: null,
        names: {},
        isSuspend: false,
        isHotKey: false,
        ERP_Item: "",
        barcode: "",
        barcode_type: 0,
        isDualMenu: false
      },
    };
  },
  
  created() {
    this.fetchProducts();
    this.updateDateTime();
    setInterval(this.updateDateTime, 1000);
    
    const storedLanguages = localStorage.getItem('languages');
    if (storedLanguages) {
      this.languages = JSON.parse(storedLanguages).reverse();
    }
  },
  
  methods: {
    async fetchProducts() {
      const token = localStorage.getItem('authToken');
      try {
        this.$root.isLoading = true; 
        this.loading = true;
        const response = await axios.get(`${this.$BASE_API_URL}/api/products`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        if (response.data.status) {
          const allProducts = response.data.data || [];
          
          this.products = this.$route.params.id 
            ? allProducts.filter(product => product.catId == this.$route.params.id)
            : allProducts;
          
          console.log('Fetched products for duplicate check:', this.products.length);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        this.$toast.error("Failed to load products");
      } finally {
        this.$root.isLoading = false;
        this.loading = false;
      }
    },
    
    getCategoryName(category) {
      if (category.languages && category.languages.length > 0) {
        return category.languages[0].name;
      }
      return "N/A";
    },
    
    handleBarcodeToggle() {
      if (!this.hasBarcode) {
        this.form.barcode_type = 0; 
      }
    },
    
    updateDateTime() {
      const now = new Date();
      this.currentDate = now.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      this.currentTime = now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
    
    handleImageUpload(event) {
      const file = event.target.files[0];
      
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
        
        this.form.image = file;
      } else {
        this.imagePreview = null;
        this.form.image = null;
      }
    },
    
    cancelForm() {
      this.$router.go(-1);
      this.form = {
        catId: "",
        cost: "0",
        price: "",
        prepare: "0",
        image: null,
        names: {},
        isSuspend: false,
        isHotKey: false,
        ERP_Item: "",
        barcode: "",
        isDualMenu: false
      };
    },
    
    async submitForm() {
      try {
        this.$root.isLoading = true; 
        this.loading = true;
        const formData = new FormData();
        
        formData.append('catId', this.$route.params.id);
        formData.append('cost', this.form.cost || "0");
        formData.append('price', this.form.price);
        formData.append('prepare', this.form.prepare || "0");
        formData.append('isSuspend', this.form.isSuspend ? "1" : "0");
        formData.append('isHotKey', this.form.isHotKey ? "1" : "0");
        formData.append('ERP_Item', this.form.ERP_Item || "");
        formData.append('barcode', this.form.barcode || "");
        formData.append('isDualMenu', this.form.isDualMenu ? "1" : "0");
        formData.append('barcode_type', this.hasBarcode ? this.form.barcode_type.toString() : "0");
        
        this.languages.forEach((language, index) => {
          formData.append(`names[${index}][langId]`, language.id.toString());
          formData.append(`names[${index}][name]`, this.form.names[language.id] || '');
        });
        
        if (this.form.image) {
          formData.append('image', this.form.image);
        }
        
        // Check for duplicate
        if (this.products && this.products.length > 0) {
          const isDuplicate = this.products.some(product => {
            if (!product.languages || !Array.isArray(product.languages)) return false;
            
            return product.languages.some(lang => {
              const newName = this.form.names[lang.langId];
              return newName && lang.name === newName;
            });
          });

          if (isDuplicate) {
            this.$toast.error('A product with the same name in at least one language already exists');
            return;
          }
        }
        
        const token = localStorage.getItem('authToken');
        
        const response = await fetch(`${this.$BASE_API_URL}/api/products`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
          body: formData,
        });
        
        if (response.ok) {
          this.$toast.success('Product created successfully!');
          this.$router.push(`/catproducts/${this.$route.params.id}`);
        } else {
          const errorData = await response.json();
          this.$toast.error(errorData.message || 'Failed to create product');
        }
      } catch (error) {
        console.error('Error creating product:', error);
        this.$toast.error('An error occurred while creating the product');
      } finally {
        this.$root.isLoading = false;
        this.loading = false;
      }
    },

    // EXCEL TEMPLATE METHODS
    downloadTemplate() {
      try {
        const wb = XLSX.utils.book_new();
        
        // Create headers for all languages
        const languageHeaders = this.languages.map(lang => `Name (${lang.fullName})*`);
        
        const headers = [
          ...languageHeaders,
          'Price*',
          'ERP Item Code',
          'Barcode',
          'Has Barcode (Yes/No)',
          'Barcode Type (Quantity/Rate)',
          'Suspend Product (Yes/No)',
          'Hot Key Item (Yes/No)'
        ];
        
        // Create worksheet with just headers (no extra instruction rows)
        const wsData = [headers];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        
        // Set column widths
        ws['!cols'] = [
          ...this.languages.map(() => ({ wch: 25 })),
          { wch: 15 },
          { wch: 20 },
          { wch: 20 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 }
        ];
        
        XLSX.utils.book_append_sheet(wb, ws, 'Products');
        
        // Generate and download file
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        saveAs(blob, 'product_bulk_upload_template.xlsx');

        this.$toast.success('Template downloaded successfully!');
      } catch (error) {
        console.error('Error downloading template:', error);
        this.$toast.error('Failed to download template');
      }
    },

    triggerFileUpload() {
      this.$refs.fileInput.click();
    },

    async handleTemplateUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Reset upload results
      this.uploadProgress = 0;
      this.uploadResults = {
        show: false,
        successCount: 0,
        errorCount: 0,
        errors: []
      };
      this.showErrorDetails = false;

      try {
        const reader = new FileReader();
        
        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            
            // Get first sheet
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            
            // Convert to JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            console.log('Raw Excel data:', jsonData);
            
            // Skip header row (first row) and start from row 2
            const dataRows = jsonData.slice(1);
            
            console.log('Data rows to process:', dataRows);
            
            this.uploadProgress = 10;
            
            // Process each row
            await this.processBulkProducts(dataRows);
            
          } catch (error) {
            console.error('Error parsing Excel:', error);
            this.$toast.error('Failed to parse Excel file. Please check the format.');
          }
        };

        reader.readAsArrayBuffer(file);
      } catch (error) {
        console.error('Error reading file:', error);
        this.$toast.error('Failed to read file');
      } finally {
        // Clear file input
        event.target.value = '';
      }
    },

    async processBulkProducts(rows) {
      this.$root.isLoading = true;
      
      const totalRows = rows.length;
      let processed = 0;
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      console.log('Processing rows:', rows);

      for (const row of rows) {
        processed++;
        this.uploadProgress = Math.round((processed / totalRows) * 90) + 10;

        try {
          // Skip empty rows
          if (!row || row.length === 0 || row.every(cell => !cell || cell === '')) {
            console.log(`Skipping empty row ${processed}`);
            continue;
          }

          console.log(`Processing row ${processed}:`, row);

          // Parse row data
          const languageNames = row.slice(0, this.languages.length);
          
          // Check if all required language names are provided
          const missingLanguages = languageNames.filter(name => !name || name.toString().trim() === '');
          if (missingLanguages.length > 0) {
            throw new Error('All language name fields are required');
          }

          // Get other fields with proper indexing
          const price = row[this.languages.length];
          const erpItem = row[this.languages.length + 1] ? row[this.languages.length + 1].toString() : '';
          const barcode = row[this.languages.length + 2] ? row[this.languages.length + 2].toString() : '';
          const hasBarcode = row[this.languages.length + 3] ? row[this.languages.length + 3].toString() : 'No';
          const barcodeType = row[this.languages.length + 4] ? row[this.languages.length + 4].toString() : 'Quantity';
          const isSuspend = row[this.languages.length + 5] ? row[this.languages.length + 5].toString() : 'No';
          const isHotKey = row[this.languages.length + 6] ? row[this.languages.length + 6].toString() : 'No';

          console.log('Parsed values:', {
            languageNames,
            price,
            erpItem,
            barcode,
            hasBarcode,
            barcodeType,
            isSuspend,
            isHotKey
          });

          // Validate price
          if (!price || isNaN(parseFloat(price))) {
            throw new Error('Price is required and must be a valid number');
          }

          // Validate boolean fields
          if (!['Yes', 'No'].includes(hasBarcode)) {
            throw new Error('Has Barcode must be "Yes" or "No"');
          }
          if (hasBarcode === 'Yes' && !['Quantity', 'Rate'].includes(barcodeType)) {
            throw new Error('Barcode Type must be "Quantity" or "Rate" when Has Barcode is Yes');
          }
          if (!['Yes', 'No'].includes(isSuspend)) {
            throw new Error('Suspend Product must be "Yes" or "No"');
          }
          if (!['Yes', 'No'].includes(isHotKey)) {
            throw new Error('Hot Key Item must be "Yes" or "No"');
          }

          // Prepare form data
          const formData = new FormData();
          
          formData.append('catId', this.$route.params.id);
          formData.append('cost', '0');
          formData.append('price', parseFloat(price).toString());
          formData.append('prepare', '0');
          formData.append('isSuspend', isSuspend === 'Yes' ? '1' : '0');
          formData.append('isHotKey', isHotKey === 'Yes' ? '1' : '0');
          formData.append('ERP_Item', erpItem);
          formData.append('barcode', barcode);
          formData.append('isDualMenu', '0');
          formData.append('barcode_type', hasBarcode === 'Yes' ? (barcodeType === 'Quantity' ? '0' : '1') : '0');
          
          // Add language names
          this.languages.forEach((lang, index) => {
            if (languageNames[index] && languageNames[index].toString().trim()) {
              formData.append(`names[${index}][langId]`, lang.id.toString());
              formData.append(`names[${index}][name]`, languageNames[index].toString().trim());
            }
          });

          // Log form data for debugging
          for (let [key, value] of formData.entries()) {
            console.log(key, value);
          }

          // Check for duplicate
          if (this.products && this.products.length > 0) {
            const isDuplicate = this.products.some(product => {
              if (!product.languages) return false;
              return product.languages.some(lang => {
                const langIndex = this.languages.findIndex(l => l.id === lang.langId);
                if (langIndex === -1) return false;
                const newName = languageNames[langIndex]?.toString().trim();
                return newName && lang.name === newName;
              });
            });

            if (isDuplicate) {
              throw new Error('Product with these exact names already exists');
            }
          }

          // Submit product
          const token = localStorage.getItem('authToken');
          console.log('Sending request to API...');
          
          const response = await fetch(`${this.$BASE_API_URL}/api/products`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
            },
            body: formData,
          });

          console.log('Response status:', response.status);

          if (response.ok) {
            successCount++;
            console.log(`Product ${processed} added successfully`);
          } else {
            const errorText = await response.text();
            console.error('API Error response:', errorText);
            throw new Error(`API Error: ${errorText}`);
          }

        } catch (error) {
          errorCount++;
          errors.push(`Row ${processed + 1}: ${error.message}`);
          console.error(`Error processing row ${processed + 1}:`, error);
        }
      }

      // Update results
      this.uploadResults = {
        show: true,
        successCount,
        errorCount,
        errors
      };

      this.$root.isLoading = false;
      this.uploadProgress = 100;

      // Show summary toast
      if (errorCount === 0 && successCount > 0) {
        this.$toast.success(`Successfully added ${successCount} products!`);
      } else if (successCount > 0 && errorCount > 0) {
        this.$toast.warning(`Added ${successCount} products, ${errorCount} failed. Check details for more information.`);
      } else if (successCount === 0 && errorCount > 0) {
        this.$toast.error(`Failed to add any products. ${errorCount} errors occurred.`);
      } else if (successCount === 0 && errorCount === 0) {
        this.$toast.info('No valid data rows found in the uploaded file.');
      }

      // Refresh products list
      await this.fetchProducts();
    }
  },
};
</script>
  
<style scoped>
.class-1 {
  font-family: 'Poppins', sans-serif;
}

.logo_icon {
  max-width: 100px;
}

.addproduct {
  background-color: #4caf50;
  color: #fff;
}

.gap-2 {
  gap: 0.5rem;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-success:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.progress {
  height: 20px;
  border-radius: 4px;
}

.progress-bar {
  background-color: #4caf50;
  transition: width 0.3s ease;
}

.card {
  border: 1px solid rgba(0,0,0,.125);
  border-radius: 0.25rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.card-title {
  margin-bottom: 1rem;
  font-size: 1.1rem;
  font-weight: 500;
}

.position-relative {
  position: relative;
}

.d-none {
  display: none;
}

.alert {
  padding: 1rem;
  border-radius: 0.25rem;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffeeba;
  color: #856404;
}

.btn-link {
  color: #007bff;
  text-decoration: none;
}

.btn-link:hover {
  text-decoration: underline;
}

.text-danger {
  color: #dc3545;
}

.small {
  font-size: 0.875rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

.mb-4 {
  margin-bottom: 1.5rem;
}

.ml-4 {
  margin-left: 1.5rem;
}
</style>